import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CUTOFF_SCORE,
  STRONG_HIRE_SCORE,
  getSessionScore,
  getSessionRecommendation,
  recommendationLabelFromScore,
  recCriteriaText,
  getRecommendationBadge,
} from "../../utils/hiringRecommendation";

function isSessionCompleted(s) {
  return String(s?.status || "").toLowerCase() === "completed";
}

/** All Candidates stage: Completed · Not completed · Invited */
function allCandidatesStatus(s) {
  const raw = String(s?.status || "scheduled").toLowerCase();
  if (raw === "completed") return { label: "Completed", color: "#16A34A" };
  if (["scheduled", "invited"].includes(raw)) return { label: "Invited", color: "#2563EB" };
  return { label: "Not completed", color: "#D97706" };
}

function displaySessionScore(s) {
  if (!isSessionCompleted(s)) return "—";
  const scoreNum = getSessionScore(s);
  return scoreNum > 0 ? scoreNum.toFixed(1) : "—";
}

function PopupToolbarFilter({
  options,
  selected,
  onToggleOption,
  onClear,
  isOpen,
  onToggle,
  onClose,
  allLabel = "All",
}) {
  const wrapRef = useRef(null);
  const active = selected.size > 0;
  const triggerLabel =
    selected.size === 0
      ? allLabel
      : selected.size === 1
        ? options.find((o) => selected.has(o.value))?.label || "1 selected"
        : `${selected.size} selected`;

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen, onClose]);

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 148, flexShrink: 0 }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "9px 12px",
          borderRadius: 8,
          border: `1.5px solid ${active ? "rgba(37,99,235,0.5)" : "#E2E8F0"}`,
          background: active ? "rgba(37,99,235,0.07)" : "#fff",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
          outline: "none",
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span style={{
          fontSize: 13,
          color: active ? "#2563EB" : "#64748B",
          fontWeight: active ? 600 : 400,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {triggerLabel}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"
          style={{ flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 50,
            background: "#fff",
            border: "1.5px solid #E2E8F0",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 220,
            width: 220,
            maxHeight: 240,
            overflowY: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {active && (
            <button
              type="button"
              onClick={() => onClear()}
              style={{
                display: "block", width: "100%", padding: "8px 14px",
                background: "transparent", border: "none", borderBottom: "1px solid #E2E8F0",
                cursor: "pointer", textAlign: "left", fontSize: 11, fontWeight: 700,
                color: "#EF4444", fontFamily: "inherit",
              }}
            >
              ✕ Clear selection
            </button>
          )}
          {options.map((opt) => {
            const checked = selected.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => onToggleOption(opt.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "9px 14px",
                  background: checked ? "rgba(37,99,235,0.08)" : "transparent",
                  border: "none", borderBottom: "1px solid #F1F5F9",
                  cursor: "pointer", textAlign: "left", fontFamily: "inherit",
                }}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                  border: `2px solid ${checked ? "#2563EB" : "#CBD5E1"}`,
                  background: checked ? "#2563EB" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {checked && (
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#0F172A", lineHeight: 1.3 }}>{opt.label}</div>
                  {opt.sub ? (
                    <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>{opt.sub}</div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlainHeader({ label }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "10px 14px",
        fontSize: 11,
        fontWeight: 700,
        color: "#64748B",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: "1px solid #E2E8F0",
        background: "#F8FAFC",
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      {label}
    </th>
  );
}

/**
 * Interview funnel visualization.
 * @param {object[]} sessions - filtered session list
 * @param {boolean} showDetails - when true, show criteria subtitle + conversion cards
 */
export default function InterviewFunnel({ sessions = [], showDetails = false }) {
  const navigate = useNavigate();
  const [funnelPopup, setFunnelPopup] = useState(null);
  const [popupFilter, setPopupFilter] = useState(() => new Set());
  const [openHeaderFilter, setOpenHeaderFilter] = useState(null);
  const popupRef = useRef(null);

  const buckets = useMemo(() => {
    const statusOf = (s) => String(s.status || "").toLowerCase();
    const all = sessions || [];
    const invited = all.filter((s) =>
      ["scheduled", "invited", "in_progress", "completed", "terminated"].includes(statusOf(s))
    );
    const invitedList = invited.length ? invited : all;
    const completed = all.filter((s) => statusOf(s) === "completed");
    const scoredPool = (completed.length ? completed : all.filter((s) => getSessionScore(s) > 0)).slice();
    const recommendation = scoredPool.sort((a, b) => getSessionScore(b) - getSessionScore(a));
    const strongHire = recommendation.filter((s) => getSessionScore(s) >= STRONG_HIRE_SCORE);
    return {
      all,
      invited: invitedList,
      completed,
      recommendation,
      strongHire,
      topN: strongHire.length,
    };
  }, [sessions]);

  const pctOf = (val, base) => (base > 0 ? Math.round((val / base) * 100) : 0);
  const invitation = buckets.invited.length ? buckets.invited : buckets.all;
  const { completed, recommendation, strongHire, topN } = buckets;
  const overallRate = pctOf(strongHire.length, completed.length || buckets.all.length || 1);
  const recCriteria = recCriteriaText();

  const openFunnelPopup = (title, color, rows, opts = {}) => {
    setPopupFilter(new Set());
    setOpenHeaderFilter(null);
    setFunnelPopup({
      title,
      color,
      rows: rows || [],
      ranked: !!opts.ranked,
      subtitle: opts.subtitle || "",
      hideRecommendation: !!opts.hideRecommendation,
      completionStatus: !!opts.completionStatus,
      popupMode: opts.popupMode || "default",
    });
  };

  const stages = [
    {
      key: "invitation",
      label: "All Candidates",
      val: invitation.length,
      color: "#1D4ED8",
      sub: null,
      rows: invitation,
      popupMode: "allCandidates",
    },
    {
      key: "completed",
      label: "Candidates status",
      val: completed.length,
      color: "#0284C7",
      sub: null,
      rows: invitation,
      hideRecommendation: true,
      completionStatus: true,
      popupMode: "candidateStatus",
      subtitle: `${completed.length} completed · ${Math.max(0, invitation.length - completed.length)} not completed`,
    },
    {
      key: "recommendation",
      label: "Recommendation",
      val: recommendation.length,
      color: "#7C3AED",
      sub: null,
      rows: recommendation,
      ranked: true,
      popupMode: "recommendation",
      subtitle: `Ranked by score · ${recCriteria}`,
    },
    {
      key: "strong",
      label: "Strong Hire",
      val: strongHire.length,
      color: "#EA580C",
      sub: recommendation.length ? `(≥ ${STRONG_HIRE_SCORE})` : null,
      rows: strongHire,
      ranked: true,
      popupMode: "strongHire",
      subtitle: `Score ≥ ${STRONG_HIRE_SCORE}${topN ? ` · ${topN} candidate${topN === 1 ? "" : "s"}` : ""}`,
    },
  ];

  const conversions = [
    {
      title: "Invite → Complete",
      from: invitation.length,
      to: completed.length,
      pct: pctOf(completed.length, invitation.length),
      color: "#0284C7",
      popupTitle: "Completed interviews",
      rows: completed,
      icon: (
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
    },
    {
      title: "Complete → Recommendation",
      from: completed.length,
      to: recommendation.length,
      pct: pctOf(recommendation.length, completed.length),
      color: "#7C3AED",
      popupTitle: "Recommendation",
      rows: recommendation,
      ranked: true,
      subtitle: recCriteria,
      popupMode: "recommendation",
      icon: (
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      ),
    },
    {
      title: "Recommendation → Strong Hire",
      from: recommendation.length,
      to: strongHire.length,
      pct: pctOf(strongHire.length, recommendation.length || 1),
      color: "#EA580C",
      popupTitle: "Strong Hire",
      rows: strongHire,
      ranked: true,
      subtitle: `Score ≥ ${STRONG_HIRE_SCORE}`,
      popupMode: "strongHire",
      icon: (
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
    },
  ];

  const W = 920;
  const CY = 78;
  const LEFT_H = 112;
  const RIGHT_H = 42;
  const GAP = 3;
  const n = stages.length;
  const stageW = (W - GAP * (n - 1)) / n;

  const heightAt = (x) => {
    const t = x / W;
    return LEFT_H + (RIGHT_H - LEFT_H) * t;
  };

  const openStage = (s) =>
    openFunnelPopup(s.label, s.color, s.rows, {
      ranked: !!s.ranked,
      subtitle: s.subtitle,
      hideRecommendation: !!s.hideRecommendation,
      completionStatus: !!s.completionStatus,
      popupMode: s.popupMode,
    });

  const toggleFilterOption = (val) => {
    setPopupFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const clearFilter = () => setPopupFilter(new Set());

  const filteredPopupRows = useMemo(() => {
    if (!funnelPopup) return [];
    let rows = funnelPopup.rows;

    if (funnelPopup.popupMode === "candidateStatus" && popupFilter.size > 0) {
      rows = rows.filter((s) => {
        const done = isSessionCompleted(s);
        if (popupFilter.has("completed") && done) return true;
        if (popupFilter.has("not_completed") && !done) return true;
        return false;
      });
    }

    if (funnelPopup.popupMode === "recommendation" && popupFilter.size > 0) {
      rows = rows.filter((s) => popupFilter.has(getSessionRecommendation(s).key));
    }

    if (funnelPopup.ranked) {
      rows = [...rows].sort((a, b) => getSessionScore(b) - getSessionScore(a));
    }

    return rows;
  }, [funnelPopup, popupFilter]);

  const statusFilterOptions = [
    { value: "completed", label: "Completed", sub: "Finished the interview" },
    { value: "not_completed", label: "Not completed", sub: "Did not finish the interview" },
  ];

  const recFilterOptions = [
    { value: "strong_yes", label: "Strong Hire", sub: `Score ≥ ${STRONG_HIRE_SCORE}` },
    { value: "yes", label: "Hire", sub: `Score ≥ ${CUTOFF_SCORE}` },
    { value: "no", label: "No Hire", sub: `Score < ${CUTOFF_SCORE}` },
  ];

  return (
    <>
      <div
        style={{
          background: "#fff",
          border: "1px solid #E2E8F0",
          borderRadius: 14,
          padding: "22px 24px 20px",
          marginBottom: 16,
          boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", marginBottom: showDetails ? 4 : 0 }}>Interview Funnel</div>
            {showDetails && (
              <div style={{ fontSize: 12, color: "#64748B" }}>
                All Candidates → Candidates status → Recommendation. No Hire {"<"} {CUTOFF_SCORE} · Hire ≥ {CUTOFF_SCORE} · Strong Hire ≥ {STRONG_HIRE_SCORE}. Click a stage to view the list.
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#2563EB", lineHeight: 1 }}>{overallRate}%</div>
            <div style={{ fontSize: 11, color: "#64748B", fontWeight: 600, marginTop: 3 }}>Overall Strong Hire Rate</div>
            <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>(Completed → Strong Hire)</div>
          </div>
        </div>

        <svg width="100%" viewBox={`0 0 ${W} ${CY * 2 + 8}`} style={{ display: "block", overflow: "visible" }}>
          {stages.map((s, i) => {
            const x1 = i * (stageW + GAP);
            const x2 = x1 + stageW;
            const h1 = heightAt(x1);
            const h2 = heightAt(x2);
            const y1t = CY - h1 / 2;
            const y1b = CY + h1 / 2;
            const y2t = CY - h2 / 2;
            const y2b = CY + h2 / 2;
            const points = `${x1},${y1t} ${x2},${y2t} ${x2},${y2b} ${x1},${y1b}`;
            const midX = (x1 + x2) / 2;
            const midH = (h1 + h2) / 2;
            const showSub = s.sub && midH > 58;

            return (
              <g
                key={s.key}
                style={{ cursor: "pointer", outline: "none" }}
                onClick={(e) => {
                  e.currentTarget.blur();
                  openStage(s);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openStage(s);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <polygon points={points} fill={s.color} opacity={s.val === 0 ? 0.45 : 1} />
                <text x={midX} y={CY - (showSub ? 16 : 10)} textAnchor="middle" fontSize={11} fontWeight={700} fill="#fff" opacity={0.95} style={{ pointerEvents: "none" }}>
                  {s.label}
                </text>
                <text x={midX} y={CY + (showSub ? 8 : 12)} textAnchor="middle" fontSize={midH > 70 ? 22 : 18} fontWeight={800} fill="#fff" style={{ pointerEvents: "none" }}>
                  {s.val}
                </text>
                {showSub && (
                  <text x={midX} y={CY + 26} textAnchor="middle" fontSize={10} fontWeight={600} fill="#fff" opacity={0.9} style={{ pointerEvents: "none" }}>
                    {s.sub}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {showDetails && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 18 }}>
            {conversions.map((c) => (
              <button
                key={c.title}
                type="button"
                onClick={() =>
                  openFunnelPopup(c.popupTitle, c.color, c.rows, {
                    ranked: !!c.ranked,
                    subtitle: c.subtitle,
                    popupMode: c.popupMode,
                  })
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  borderRadius: 12,
                  border: "1px solid #E2E8F0",
                  background: "#fff",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  transition: "border-color .15s, box-shadow .15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = c.color;
                  e.currentTarget.style.boxShadow = `0 4px 14px ${c.color}22`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#E2E8F0";
                  e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,0.04)";
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: c.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {c.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#64748B", marginBottom: 3 }}>{c.title}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
                    {c.from} → {c.to}
                    <span style={{ color: c.color, marginLeft: 8 }}>• {c.pct}%</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {funnelPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setFunnelPopup(null);
          }}
        >
          <div
            ref={popupRef}
            style={{
              background: "#fff",
              borderRadius: 14,
              width: "min(920px, 96vw)",
              maxHeight: "88vh",
              overflow: openHeaderFilter ? "visible" : "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: "1px solid #E2E8F0",
                gap: 16,
                overflow: "visible",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: funnelPopup.color, display: "inline-block" }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>{funnelPopup.title}</span>
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                  {filteredPopupRows.length} of {funnelPopup.rows.length} candidate{funnelPopup.rows.length === 1 ? "" : "s"}
                  {funnelPopup.subtitle ? ` · ${funnelPopup.subtitle}` : ""}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                {funnelPopup.popupMode === "candidateStatus" && (
                  <PopupToolbarFilter
                    options={statusFilterOptions}
                    selected={popupFilter}
                    onToggleOption={toggleFilterOption}
                    onClear={clearFilter}
                    isOpen={openHeaderFilter === "status"}
                    onToggle={() => setOpenHeaderFilter((prev) => (prev === "status" ? null : "status"))}
                    onClose={() => setOpenHeaderFilter(null)}
                    allLabel="All Statuses"
                  />
                )}
                {funnelPopup.popupMode === "recommendation" && (
                  <PopupToolbarFilter
                    options={recFilterOptions}
                    selected={popupFilter}
                    onToggleOption={toggleFilterOption}
                    onClear={clearFilter}
                    isOpen={openHeaderFilter === "recommendation"}
                    onToggle={() => setOpenHeaderFilter((prev) => (prev === "recommendation" ? null : "recommendation"))}
                    onClose={() => setOpenHeaderFilter(null)}
                    allLabel="All Recommendations"
                  />
                )}
                <button
                  type="button"
                  onClick={() => {
                    setOpenHeaderFilter(null);
                    setFunnelPopup(null);
                  }}
                  style={{
                    border: "none",
                    background: "#F1F5F9",
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: 16,
                    color: "#64748B",
                    fontFamily: "inherit",
                    flexShrink: 0,
                  }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            <div style={{ overflowY: "auto", flex: 1 }}>
              {filteredPopupRows.length === 0 ? (
                <div style={{ padding: 32, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
                  No candidates match this filter.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {funnelPopup.ranked && <PlainHeader label="Rank" />}
                      <PlainHeader label="Candidate" />
                      <PlainHeader label="Email" />
                      <PlainHeader label="Position" />
                      {!funnelPopup.ranked && <PlainHeader label="Status" />}
                      <PlainHeader label="Score" />
                      {!funnelPopup.hideRecommendation && <PlainHeader label="Recommendation" />}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPopupRows.map((s, idx) => {
                      const name = s.candidate_name ?? s.name ?? "—";
                      const email = s.candidate_email ?? s.email ?? "—";
                      const campaign = s.campaign_name ?? s.campaign ?? "—";
                      const score = displaySessionScore(s);
                      const scoreNum = isSessionCompleted(s) ? getSessionScore(s) : 0;
                      const completed = isSessionCompleted(s);

                      let statusLabel = String(s.status || "scheduled").replace(/_/g, " ");
                      let statusColor = "#64748B";
                      let statusWeight = 400;

                      if (funnelPopup.popupMode === "allCandidates") {
                        const st = allCandidatesStatus(s);
                        statusLabel = st.label;
                        statusColor = st.color;
                        statusWeight = 700;
                      } else if (funnelPopup.completionStatus) {
                        statusLabel = completed ? "Completed" : "Not completed";
                        statusColor = completed ? "#16A34A" : "#D97706";
                        statusWeight = 700;
                      }

                      const badge = getRecommendationBadge(
                        funnelPopup.ranked ? { overall_score: scoreNum } : s
                      );
                      const recLabel = completed && scoreNum > 0
                        ? (funnelPopup.ranked
                          ? recommendationLabelFromScore(scoreNum) ?? badge.label
                          : badge.label)
                        : null;

                      return (
                        <tr
                          key={s.id || `${email}-${idx}`}
                          style={{ borderBottom: "1px solid #F1F5F9", cursor: s.id ? "pointer" : "default" }}
                          onClick={() => {
                            setFunnelPopup(null);
                            navigate("/admin/interviews");
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#F8FAFC";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {funnelPopup.ranked && (
                            <td style={{ padding: "11px 14px", fontWeight: 700, color: "#64748B", fontSize: 12 }}>#{idx + 1}</td>
                          )}
                          <td style={{ padding: "11px 14px", fontWeight: 600, color: "#0F172A", fontSize: 13 }}>{name}</td>
                          <td style={{ padding: "11px 14px", color: "#64748B", fontSize: 12 }}>{email}</td>
                          <td style={{ padding: "11px 14px", color: "#334155", fontSize: 12 }}>{campaign}</td>
                          {!funnelPopup.ranked && (
                            <td
                              style={{
                                padding: "11px 14px",
                                fontSize: 12,
                                fontWeight: statusWeight,
                                textTransform: funnelPopup.completionStatus || funnelPopup.popupMode === "allCandidates" ? "none" : "capitalize",
                                color: statusColor,
                              }}
                            >
                              {statusLabel}
                            </td>
                          )}
                          <td style={{ padding: "11px 14px", fontWeight: 700, fontSize: 13, color: "#0F172A" }}>{score}</td>
                          {!funnelPopup.hideRecommendation && (
                            <td style={{ padding: "11px 14px" }}>
                              {recLabel ? (
                                <span
                                  style={{
                                    display: "inline-block",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    padding: "3px 10px",
                                    borderRadius: 20,
                                    background: badge.bg,
                                    color: badge.color,
                                  }}
                                >
                                  {recLabel}
                                </span>
                              ) : (
                                <span style={{ color: "#94A3B8", fontSize: 12 }}>—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
